import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Box from "@mui/material/Box";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import { axiosReqConfig } from "../functions/axiosRequest";
import { API_IP, STRIPE_KEY } from "../global_variables";
import { format, getDay, set } from "date-fns";
import { enGB, is } from "date-fns/locale";
import PaymentModal from "./AccountComponents/PaymentModal";
import "./BookingHistory.css";

const BookingScreen = () => {
  const [activeTab, setActiveTab] = useState("Frequently Asked Questions");
  const [bookings, setBookings] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(7);
  const [paymentVisible, setPaymentVisible] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [totalPrice, setTotalPrice] = useState("");
  const [customerSession, setCustomerSession] = useState("");
  const [selectedBooking, setSelectedBooking] = useState({});
  const [refresh, isRefresh] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingBookings, setIsFetchingBookings] = useState(true);
  const stripePromise = loadStripe(STRIPE_KEY);
  const togglePayment = () => {
    setPaymentVisible(!paymentVisible);
  };
  const toggleRefresh = () => {
    isRefresh(!refresh);
  };

  const totalItems = bookings.length;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = bookings.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const steps = [
    "Booking",
    "Wait for Response (Provider)",
    "Payment",
    "Completion",
  ];
  const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
      top: 22,
    },
    [`&.${stepConnectorClasses.active}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        backgroundImage:
          "linear-gradient(136deg, rgb(255, 235, 59) 0%, rgb(255, 204, 0) 50%, rgb(230, 200, 0) 50%)",
      },
    },
    [`&.${stepConnectorClasses.completed}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        backgroundImage:
          "linear-gradient(136deg, rgb(200, 255, 200) 0%, rgb(144, 238, 144) 50%, rgb(46, 139, 87) 100%)",
      },
    },
    [`& .${stepConnectorClasses.line}`]: {
      height: 3,
      border: 0,
      backgroundColor: "#eaeaf0",
      borderRadius: 1,
      ...theme.applyStyles("dark", {
        backgroundColor: theme.palette.grey[800],
      }),
    },
  }));
  const ColorlibStepIconRoot = styled("div")(({ theme }) => ({
    backgroundColor: "#ccc",
    zIndex: 1,
    color: "#fff",
    width: 50,
    height: 50,
    display: "flex",
    borderRadius: "50%",
    justifyContent: "center",
    alignItems: "center",
    ...theme.applyStyles("dark", {
      backgroundColor: theme.palette.grey[700],
    }),
    variants: [
      {
        props: ({ ownerState }) => ownerState.active,
        style: {
          backgroundImage: "linear-gradient(rgb(230, 200, 0) 50%)",
          boxShadow: "0 4px 10px 0 rgba(0,0,0,.25)",
        },
      },
      {
        props: ({ ownerState }) => ownerState.completed,
        style: {
          backgroundImage: "linear-gradient(rgb(46, 139, 87) 100%)",
          boxShadow: "0 4px 10px 0 rgba(0,0,0,.25)",
        },
      },
    ],
  }));

  function ColorlibStepIcon(props) {
    const { active, completed, className } = props;

    const icons = {
      1: (
        <img
          src={require(`../images/Booking-icon.png`)}
          className="BookingIcon"
        />
      ),
      2: (
        <img src={require(`../images/wait-icon.png`)} className="BookingIcon" />
      ),
      3: (
        <img
          src={require(`../images/payment-icon.png`)}
          className="BookingIcon"
        />
      ),
      4: (
        <img
          src={require(`../images/completion-icon.png`)}
          className="BookingIcon"
        />
      ),
    };

    return (
      <ColorlibStepIconRoot
        ownerState={{ completed, active }}
        className={className}
      >
        {icons[String(props.icon)]}
      </ColorlibStepIconRoot>
    );
  }

  const fetchBookingInfo = useCallback(async () => {
    setIsFetchingBookings(true);
    try {
      const res = await axiosReqConfig(`${API_IP}/api/dashboard`, "get", {});
      const data = res?.ok ? res.data : null;
      setBookings(Array.isArray(data?.bookings) ? data.bookings : []);
      setIsFetchingBookings(false);
      return data;
    } catch (error) {
      console.error("Error fetching booking info:", error);
      setIsFetchingBookings(false);
      return null;
    }
  }, [refresh]);

  function formatDateWithDay(dateString) {
    const date = new Date(dateString);
    const formattedDate = format(date, "d MMMM yyyy", { locale: enGB });
    const dayOfWeek = format(date, "EEE", { locale: enGB });
    return `${dayOfWeek} ${formattedDate}`;
  }

  async function requestPayment(booking) {
    setIsLoading(true);
    try {
      const res = await axiosReqConfig(`${API_IP}/api/payment`, "post", {
        booking_id: booking.booking_id,
        service_id: booking.care_service_id,
        provider_id: booking.provider_id,
      });
      if (!res?.ok) {
        setIsLoading(false);
        return;
      }
      const data = res.data;

      const {
        clientSecret,
        totalPrice,
        transactionId,
        ephemeralKey,
        customer,
        customerSession,
      } = data;
      setSelectedBooking({
        ...booking,
        transactionId: transactionId,
      });
      setTotalPrice(totalPrice);
      setCustomerSession(customerSession);
      setClientSecret(clientSecret);
      setPaymentVisible(true);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log(console.error());
    }
  }

  const BookingInfoDiv = ({ booking }) => {
    const [isFooterExpand, setIsFooterExpand] = useState(false);
    const toggleFooterExpand = () => {
      setIsFooterExpand(!isFooterExpand);
    };
    const start_date = formatDateWithDay(booking.start_date);
    let currentStep = 0;
    if (!booking.provider_confirmation && !booking.paid) {
      currentStep = 1;
    } else if (!booking.paid) {
      currentStep = 2;
    } else if (booking.paid && booking.acceptance === true) {
      currentStep = 4;
    }

    return (
      <div className="BS-card">
        <div className="BS-card-header">
          <div className="BS-header-left">
            <img
              src={require(`../images/provider-icon.png`)}
              className="BS-provider-icon"
            />
            <h4>
              {booking.provider_name} -{" "}
              {booking.acceptance === null ? (
                <span className="BS-badge BS-badge-pending">
                  Pending{" "}
                  <img
                    alt="Booking Pending Icon"
                    src={require("../images/booking_pending_icon.png")}
                  />{" "}
                </span>
              ) : (
                <>
                  {!booking.acceptance ? (
                    <span className="BS-badge BS-badge-denied">
                      Declined{" "}
                      <img
                        alt="Booking Pending Icon"
                        src={require("../images/booking_denied_icon.png")}
                      />
                    </span>
                  ) : (
                    <span className="BS-badge BS-badge-accepted">
                      Accepted{" "}
                      <img
                        alt="Booking Pending Icon"
                        src={require("../images/booking_accepted_icon.webp")}
                      />
                    </span>
                  )}
                </>
              )}
            </h4>
          </div>
          <div className="BS-header-right">
            <span>ID: {booking.booking_id}</span>
          </div>
        </div>
        <div className="BS-card-body">
          <div className="BS-info-block">
            <span className="BS-info-label">Start Date</span>
            <h2 className="BS-info-value">{start_date}</h2>
            <span className="BS-info-label">Service</span>
            <span className="BS-info-highlight">{booking.care_service_name}</span>
          </div>
          <div className="BS-stepper-block">
            <Stack sx={{ width: "100%" }} spacing={4}>
              <Stepper
                alternativeLabel
                activeStep={currentStep}
                connector={<ColorlibConnector />}
              >
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel StepIconComponent={ColorlibStepIcon}>
                      {label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Stack>
          </div>
          <div className="BS-info-block">
            <span className="BS-info-label">Member</span>
            <h3 className="BS-info-value">{`${booking.first_name} ${booking.last_name}`}</h3>
            <span className="BS-info-label">Location</span>
            <span className="BS-info-highlight">Victoria</span>
          </div>
        </div>
        <div className="BS-card-footer">
          <div className="BS-footer-actions">
            <div className="BS-footer-left">
              <button
                onClick={toggleFooterExpand}
                className="BS-action-btn BS-btn-outline"
              >
                {isFooterExpand ? "Less Details" : "More Details"}
              </button>
            </div>
            <div className="BS-footer-right">
            {currentStep == 1 ? (
              <>
                {booking.acceptance === null ? (
                  <button className="BS-action-btn BS-btn-disabled" disabled>
                    Wait For Provider
                  </button>
                ) : (
                  <button className="BS-action-btn BS-btn-disabled" disabled>
                    Provider Has Declined
                  </button>
                )}
              </>
            ) : (
              <></>
            )}
            {currentStep == 2 ? (
              <button
                onClick={() => requestPayment(booking)}
                className="BS-action-btn BS-btn-primary"
                disabled={isLoading}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}
              >
                {isLoading && (
                  <img
                    src={require("../images/loading_icon.gif")}
                    alt="Loading"
                    style={{ width: "20px", height: "20px" }}
                  />
                )}
                {isLoading ? "Processing..." : "Pay To Process"}
              </button>
            ) : (
              <></>
            )}
            </div>
          </div>
        </div>
        {isFooterExpand ? (
          <div className="BS-card-expand">
            <div className="BS-expand-section">
              <h5>
                Provider has <span className="BS-text-green">accepted</span> these dates:
              </h5>
              {booking.accepted_dates.map((date, index) => {
                const formatDate = formatDateWithDay(date);
                return (
                  <p key={index}>
                    <img
                      src={require(`../images/date-confirmed.png`)}
                      className="BS-date-icon"
                    />
                    {formatDate} ({booking.accepted_start_times[index]} -{" "}
                    {booking.accepted_end_times[index]})
                  </p>
                );
              })}
            </div>
            <div className="BS-expand-section">
              <h5>
                Provider has <span className="BS-text-red">declined</span> these dates:
              </h5>
              {booking.declined_dates.map((date, index) => {
                if (booking.acceptance !== null) {
                  const formatDate = formatDateWithDay(date);
                  return (
                    <p key={index}>
                      <img
                        src={require(`../images/booking_denied_icon.png`)}
                        className="BS-date-icon"
                      />
                      {formatDate}
                    </p>
                  );
                } else {
                  return <></>;
                }
              })}
            </div>
          </div>
        ) : (
          <></>
        )}
      </div>
    );
  };

  useEffect(() => {
    fetchBookingInfo();
  }, [fetchBookingInfo]);
  return (
    <>
      {isLoading && !paymentVisible && (
        <div className="BS-loading-overlay">
          <div className="BS-loading-modal">
            <img src={require("../images/loading_icon.gif")} alt="Loading" />
            <p>Preparing secure payment...</p>
          </div>
        </div>
      )}
      {paymentVisible ? (
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            customerSessionClientSecret: customerSession,
          }}
        >
          <PaymentModal
            showModal={paymentVisible}
            handleClose={togglePayment}
            clientSecret={clientSecret}
            customerSession={customerSession}
            totalPrice={totalPrice}
            selectedBooking={selectedBooking}
            toggleRefresh={toggleRefresh}
          />
        </Elements>
      ) : (
        <></>
      )}
      <div className="BS-container screen-container">
        <header className="screen-header">
          <div className="screen-header-text">
            <h1 className="screen-title">Booking History</h1>
            <p className="screen-subtitle">Track and manage your care service requests</p>
          </div>
        </header>
        <div className="BS-filters">
          <div className="BS-filters-left">
            <button className="BS-filter-btn BS-filter-active">
              In Process
            </button>
            <button className="BS-filter-btn">
              Completed
            </button>
            <span className="BS-filter-count">
              {bookings.length} requests
            </span>
          </div>

          <div className="BS-pagination">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="BS-page-btn"
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(
              (pageNumber) => (
                <button
                  key={pageNumber}
                  onClick={() => setCurrentPage(pageNumber)}
                  className={`BS-page-btn ${
                    currentPage === pageNumber
                      ? "BS-page-active"
                      : ""
                  }`}
                >
                  {pageNumber}
                </button>
              )
            )}
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="BS-page-btn"
            >
              Next
            </button>
          </div>
        </div>
        <div className="BS-list-container">
          {isFetchingBookings ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "50px 0" }}>
              <img
                src={require("../images/loading_icon.gif")}
                alt="Loading bookings"
                style={{ width: "50px", marginBottom: "15px" }}
              />
              <span style={{ color: "#666", fontSize: "16px" }}>Loading booking history...</span>
            </div>
          ) : currentItems.length > 0 ? (
            currentItems.map((booking) => (
              <BookingInfoDiv key={booking.id} booking={booking} />
            ))
          ) : (
            <div style={{ textAlign: "center", padding: "50px 0", color: "#888", fontStyle: "italic" }}>
              No booking requests found.
            </div>
          )}
        </div>
        <div className="BS-pagination BS-pagination-bottom">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="BS-page-btn"
          >
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(
            (pageNumber) => (
              <button
                key={pageNumber}
                onClick={() => setCurrentPage(pageNumber)}
                className={`BS-page-btn ${
                  currentPage === pageNumber
                    ? "BS-page-active"
                    : ""
                }`}
              >
                {pageNumber}
              </button>
            )
          )}
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
            className="BS-page-btn"
          >
            Next
          </button>
        </div>
      </div>
    </>
  );
};

export default BookingScreen;
